import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";
import { useDebounce } from "@uidotdev/usehooks";
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  CurrencyPoundIcon,
  PhotoIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import { createColumnHelper } from "@tanstack/react-table";

import { PageMode } from "../../models/PagedTable";
import {
  IVehicle,
  PartsStatusDropdowns,
  PartsStatusValues,
} from "../../models/Vehicle";
import { LeadSource } from "../../models/Lead";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import PagedTable from "../shared/Tables/PagedTable";
import VehicleLocationDropdown from "../vehicles/VehicleLocationDropdown";
import AwaitingCheckInCell from "./AwaitingCheckInCell";
import AwaitingInventoryStatusDropdown from "./AwaitingInventoryStatusDropdown";
import CustomDatePicker from "./CustomDatePicker";
import PartsPriorityDropdown from "./PartsPriorityDropdown";
import RowCard from "./RowCard";
import VehicleImageCell from "./VehicleImageCell";
import VehicleInfoCell from "./VehicleInfoCell";
import WilmaIntelligenceCell from "./WilmaIntelligenceCell";
import YardStatusDropdown from "./YardStatusDropdown";
import { MakeCombobox } from "../parts/MakeCombobox";
import { ModelCombobox } from "../parts/ModelCombobox";
import { YearCombobox } from "../parts/YearCombobox";

import useCustomerDetails from "../../data/useCustomerDetails";
import LoadingWheel from "../shared/LoadingWheel";
import { FeatureStatus } from "../../models/Customer";
import SlimSelectCol from "../shared/Tables/SlimSelectCol";
import useNotification from "../notifications/useNotifications";

const colHelper = createColumnHelper<IVehicle>();

type TabKey = "all" | "awaitingCollection" | "awaitingCheckIn" | "checkedIn";

const tabs: { key: TabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "awaitingCollection", label: "Awaiting Collection" },
  { key: "awaitingCheckIn", label: "Awaiting Check-In" },
  { key: "checkedIn", label: "Checked In" },
];

interface AwaitingInventoryTableProps {
  // Synthetic/overriding filters injected from the page-level stats panel.
  // Recognised keys: `statFilter` (arrivingToday | arrivingTomorrow |
  // awaitingCollection | startsAndDrives) and `engineExportStatus`.
  externalFilters?: Record<string, any>;
  filterBanner?: React.ReactNode;
}

export default function AwaitingInventoryTable({
  externalFilters,
  filterBanner,
}: AwaitingInventoryTableProps = {}) {
  const [currentFilter, setCurrentFilter] = useState("0");
  const [activeTab, setActiveTab] = useState<TabKey>("all");

  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});

  const { addNotification } = useNotification();
  const navigate = useNavigate();

  const { features } = useCustomerDetails();
  const [leadManagementEnabled, setLeadManagementEnabled] = useState(false);

  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null,
  });

  // Advanced search state — mirrors the richer filter UX from the Search/Stock
  // pages so operators can pin-point a specific vehicle in the queue.
  const [vrmSearch, setVrmSearch] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [makeSearch, setMakeSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [sourceSearch, setSourceSearch] = useState<string>("All");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");

  // Debounce free-text inputs so each keystroke doesn't hammer the API.
  const debouncedVrm = useDebounce(vrmSearch, 350);
  const debouncedMinValue = useDebounce(minValue, 350);
  const debouncedMaxValue = useDebounce(maxValue, 350);

  // Sort options exposed to the operator. Each maps to an `id` that
  // `PagedTable` passes through to the backend as the sort key; bumping the
  // `sortBy` below re-mounts `PagedTable` so the new `initialSort` takes
  // effect without needing a controlled-sort API.
  const sortOptions: {
    key: string;
    label: string;
    id: string;
    desc: boolean;
  }[] = useMemo(
    () => [
      {
        key: "dateAddedDesc",
        label: "Date Added (Newest first)",
        id: "created",
        desc: true,
      },
      {
        key: "dateAddedAsc",
        label: "Date Added (Oldest first)",
        id: "created",
        desc: false,
      },
      {
        key: "valueDesc",
        label: "Estimated Value (Highest first)",
        id: "totalValueAllParts",
        desc: true,
      },
      {
        key: "valueAsc",
        label: "Estimated Value (Lowest first)",
        id: "totalValueAllParts",
        desc: false,
      },
    ],
    []
  );
  const [sortBy, setSortBy] = useState<string>("dateAddedDesc");
  const activeSort =
    sortOptions.find((o) => o.key === sortBy) ?? sortOptions[0];

  const leadSourceOptions = useMemo(
    () =>
      Object.values(LeadSource).filter(
        (ct) => isNaN(Number(ct)) && ct !== "All"
      ) as string[],
    []
  );

  const handleClearAdvancedFilters = () => {
    setVrmSearch("");
    setYearFrom("");
    setYearTo("");
    setMakeSearch("");
    setModelSearch("");
    setSourceSearch("All");
    setMinValue("");
    setMaxValue("");
  };

  // Year-range numeric helpers for cross-constraining the From/To pickers.
  const yearFromNumeric = yearFrom ? Number(yearFrom) : undefined;
  const yearToNumeric = yearTo ? Number(yearTo) : undefined;

  const cols = useMemo(
    () => [
      SlimSelectCol(),
      // Each data column uses `h-px` so that `h-full` on the inner RowCard
      // resolves to the full row height — making every card in a row share
      // a single consistent baseline regardless of content length.
      colHelper.display({
        id: "image",
        header: "Vehicle",
        meta: {
          thClassName: "!px-2 w-52",
          tdClassName: "!px-2 w-52 h-px align-top",
        },
        cell: (info) => (
          <RowCard title="Vehicle" icon={PhotoIcon}>
            <VehicleImageCell vehicle={info.row.original!} />
          </RowCard>
        ),
      }),
      colHelper.accessor("combinedMake", {
        id: "combinedMake",
        header: "Vehicle Info",
        enableSorting: false,
        meta: {
          thClassName: "w-96 min-w-[24rem] max-w-[28rem]",
          tdClassName: "w-96 min-w-[24rem] max-w-[28rem] h-px align-top",
        },
        cell: (info) => <VehicleInfoCell vehicle={info.row.original!} />,
      }),
      colHelper.accessor("checkInId", {
        id: "checkInStatus",
        header: "Check-In",
        enableSorting: false,
        meta: {
          thClassName: "w-64 min-w-[16rem]",
          tdClassName: "w-64 min-w-[16rem] h-px align-top",
        },
        cell: (info) => <AwaitingCheckInCell vehicle={info.row.original!} />,
      }),
      colHelper.display({
        id: "wilmaIntelligence",
        header: "Wilma Intelligence",
        meta: {
          thClassName: "w-80 min-w-[20rem]",
          tdClassName: "w-80 min-w-[20rem] h-px align-top",
        },
        cell: (info) => <WilmaIntelligenceCell vehicle={info.row.original!} />,
      }),
      colHelper.display({
        id: "decisions",
        header: "Decisions",
        meta: {
          thClassName: "w-56 min-w-[14rem]",
          tdClassName: "w-56 min-w-[14rem] h-px align-top",
        },
        cell: (info) => (
          <RowCard
            title="Decisions"
            icon={AdjustmentsHorizontalIcon}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-1.5">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  Parts Status
                </p>
                <AwaitingInventoryStatusDropdown
                  vehicle={info.row.original!}
                  classes="mt-0.5 h-7 w-full text-xs"
                />
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  Location
                </p>
                <VehicleLocationDropdown
                  vehicle={info.row.original!}
                  classes="mt-0.5 h-7 w-full text-xs"
                />
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  Yard Status
                </p>
                <YardStatusDropdown
                  vehicle={info.row.original!}
                  classes="mt-0.5 h-7 w-full text-xs"
                />
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  Priority
                </p>
                <PartsPriorityDropdown
                  vehicle={info.row.original!}
                  classes="mt-0.5 h-7 w-full text-xs"
                />
              </div>
            </div>

            {/* Primary action — jump straight to the vehicle's parts/jobs
                page where dismantling lists are built from assemblies and
                PartPro lookups. Sits beneath the status dropdowns so
                operators can finish their triage choices and then hand the
                vehicle straight to the yard. */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const vehicle = info.row.original!;
                if (!vehicle.id) {
                  addNotification({
                    variant: "error",
                    primaryText: "Can't open dismantling list",
                    secondaryText:
                      "This vehicle doesn't have an id yet — reload the table and try again.",
                  });
                  return;
                }
                navigate(
                  `/vehicles/${vehicle.id}?tab=jobs&scrollTo=createJobs`
                );
              }}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-2 py-1.5 text-xs font-semibold text-primary-700 shadow-sm transition hover:border-primary-300 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            >
              <WrenchScrewdriverIcon className="h-3.5 w-3.5" aria-hidden />
              Create dismantling list
            </button>
          </RowCard>
        ),
      }),
    ],
    [addNotification, navigate]
  );

  const handleDatesChange = (newDates: any) => {
    setDates(newDates);
  };

  const filters = [
    ...Object.values(PartsStatusDropdowns).filter((ct) => isNaN(Number(ct))),
    "All",
  ];

  // Translate a synthetic stat-filter key (set from the page-level intelligence
  // panel) into the concrete search body the /vehicle/partial endpoint expects.
  // Keeps the stats panel logic decoupled from the table's internal filters.
  const buildStatFilterBody = (key: string | undefined) => {
    if (!key) return {};
    const now = DateTime.now().setZone("Europe/London");

    switch (key) {
      case "arrivingToday":
        return {
          startDate: now.startOf("day").toISO(),
          endDate: now.endOf("day").toISO(),
          includeUnscheduled: false,
        };
      case "arrivingTomorrow": {
        const tomorrow = now.plus({ days: 1 });
        return {
          startDate: tomorrow.startOf("day").toISO(),
          endDate: tomorrow.endOf("day").toISO(),
          includeUnscheduled: false,
        };
      }
      case "awaitingCollection":
        return {
          includeUnscheduled: false,
          includeCompleted: false,
          includeCancelled: false,
        };
      case "startsAndDrives":
        return { vehicleStarting: true };
      // Check-in tabs — the `/vehicle/partial` endpoint should interpret
      // `checkInCompleted` as: true → only show vehicles whose check-in is
      // complete, false → only show vehicles without a completed check-in.
      case "awaitingCheckIn":
        return { checkInCompleted: false };
      case "checkedIn":
        return { checkInCompleted: true };
      default:
        return {};
    }
  };

  useEffect(() => {
    let numericValue: number | null = 0;

    if (currentFilter == "All") {
      numericValue = null;
    } else if (!isNaN(Number(currentFilter))) {
      numericValue = parseInt(currentFilter as keyof PartsStatusValues);
    } else {
      numericValue =
        PartsStatusValues[currentFilter as keyof typeof PartsStatusValues];
    }

    const {
      statFilter: externalStatFilter,
      engineExportStatus: externalExportStatus,
      ...externalRest
    } = externalFilters ?? {};

    // Prefer the externally-supplied stat filter (kept for extensibility),
    // otherwise translate the currently selected tab.
    const activeStatFilter =
      externalStatFilter ?? (activeTab === "all" ? undefined : activeTab);
    const statBody = buildStatFilterBody(activeStatFilter);

    const resolvedExportStatus =
      externalExportStatus !== undefined
        ? externalExportStatus
        : currentFilter === "All"
          ? null
          : numericValue;

    const trimmedVrm = debouncedVrm.trim();
    const minVal = debouncedMinValue !== "" ? Number(debouncedMinValue) : null;
    const maxVal = debouncedMaxValue !== "" ? Number(debouncedMaxValue) : null;

    const trimmedYearFrom = yearFrom ? Number(yearFrom) : null;
    const trimmedYearTo = yearTo ? Number(yearTo) : null;

    setAdditionalBody({
      // Sensible defaults matching the previous "visibility filters" presets
      // now that the UI toggles have been removed.
      includeCompleted: true,
      includeCancelled: true,
      includeUnscheduled: false,
      includeNoLeads: false,
      // The date picker now filters by the vehicle's date-added (created)
      // timestamp rather than the scheduled collection date. `dateFieldType`
      // is the existing backend convention used elsewhere (see Stock table)
      // so the same endpoint can route the start/end to the right column.
      dateFieldType: "Created",
      startDate: dates.startDate?.toISOString(),
      endDate: dates.endDate?.toISOString(),
      engineExportStatus: resolvedExportStatus,
      // Advanced search filters. These keys mirror those used by the search
      // /stock endpoints so the backend can share filter semantics.
      vrm: trimmedVrm || null,
      yearFrom:
        trimmedYearFrom !== null && !Number.isNaN(trimmedYearFrom)
          ? trimmedYearFrom
          : null,
      yearTo:
        trimmedYearTo !== null && !Number.isNaN(trimmedYearTo)
          ? trimmedYearTo
          : null,
      make: makeSearch || null,
      model: modelSearch || null,
      leadSource: sourceSearch && sourceSearch !== "All" ? sourceSearch : null,
      minValue: minVal !== null && !Number.isNaN(minVal) ? minVal : null,
      maxValue: maxVal !== null && !Number.isNaN(maxVal) ? maxVal : null,
      ...statBody,
      ...externalRest,
    });
  }, [
    currentFilter,
    dates,
    externalFilters,
    activeTab,
    debouncedVrm,
    yearFrom,
    yearTo,
    makeSearch,
    modelSearch,
    sourceSearch,
    debouncedMinValue,
    debouncedMaxValue,
  ]);

  useEffect(() => {
    if (features.data && !features.isLoading) {
      setLeadManagementEnabled(
        features.data.data.find(
          (f) => f.name === "Lead Management" && f.status == FeatureStatus.Enabled
        )
          ? true
          : false
      );
    }
  }, [features.data, features.isLoading]);

  if (features.isLoading) return <LoadingWheel />;

  return (
    <>
      {/* ---------- Filters card ---------- */}
      {/* Compact field sizing is applied at the card level via arbitrary
          selectors so we don't have to thread size props through every
          combobox component. The [&_input]/[&_label] rules below shrink the
          `Year / Make / Model` comboboxes to match the other filters. */}
      <div
        className={classNames(
          "mt-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm",
          "[&_label]:text-[11px] [&_label]:font-semibold [&_label]:uppercase [&_label]:tracking-wide [&_label]:text-slate-500",
          "[&_.awinv-compact_input]:h-8 [&_.awinv-compact_input]:py-1 [&_.awinv-compact_input]:pl-2.5 [&_.awinv-compact_input]:pr-8 [&_.awinv-compact_input]:text-xs"
        )}
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              Search filters
            </h3>
            <p className="text-xs text-slate-500">
              Narrow the queue by vehicle, source, value, or date added.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClearAdvancedFilters}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Clear
          </button>
        </div>

        {/* Row 1 — VRM plate (primary identifier) */}
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-60">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Registration
            </label>
            <div
              className={classNames(
                "mt-1 flex h-9 items-stretch overflow-hidden rounded-md border-2 border-black shadow-sm",
                "focus-within:ring-2 focus-within:ring-primary-500"
              )}
            >
              <span
                aria-hidden="true"
                className="flex w-5 items-center justify-center bg-blue-700 text-[9px] font-bold uppercase tracking-widest text-yellow-300"
              >
                UK
              </span>
              <input
                type="text"
                value={vrmSearch}
                onChange={(e) => setVrmSearch(e.target.value.toUpperCase())}
                placeholder="ENTER VRM"
                aria-label="Search by VRM"
                className="w-full border-0 bg-yellow-300 px-2 py-0 text-center text-sm font-bold uppercase tracking-[0.2em] text-black placeholder:text-black/40 placeholder:text-[10px] placeholder:tracking-[0.15em] focus:outline-none focus:ring-0"
                style={{
                  fontFamily:
                    '"UKNumberPlate", "Charles Wright", "Arial Black", "Helvetica Neue", sans-serif',
                }}
              />
            </div>
          </div>
        </div>

        {/* Row 2 — Year From / Year To / Make / Model / Parts Status / Source / Sort */}
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          <div className="awinv-compact">
            <YearCombobox
              label="Year From"
              currentValue={yearFrom}
              maxYear={yearToNumeric}
              onYearSelect={(year) => setYearFrom((year ?? "") as string)}
              placeholder="From..."
            />
          </div>

          <div className="awinv-compact">
            <YearCombobox
              label="Year To"
              currentValue={yearTo}
              minYear={yearFromNumeric}
              onYearSelect={(year) => setYearTo((year ?? "") as string)}
              placeholder="To..."
            />
          </div>

          <div className="awinv-compact">
            <MakeCombobox
              label="Make"
              currentValue={makeSearch}
              onMakeSelect={(make) => {
                setMakeSearch((make?.make ?? "") as string);
                setModelSearch("");
              }}
            />
          </div>

          <div className="awinv-compact">
            <ModelCombobox
              label="Model"
              make={makeSearch}
              currentValue={modelSearch}
              onModelSelect={(model) =>
                setModelSearch((model?.range ?? "") as string)
              }
            />
          </div>

          <div>
            <label>Parts Status</label>
            <select
              aria-label="Parts status filter"
              value={currentFilter}
              onChange={(e) => setCurrentFilter(e.target.value)}
              className="mt-1 block h-8 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {filters.map((filter) => (
                <option className="text-xs" key={filter} value={String(filter)}>
                  {String(filter)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Source</label>
            <select
              aria-label="Lead source filter"
              value={sourceSearch}
              onChange={(e) => setSourceSearch(e.target.value)}
              className="mt-1 block h-8 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="All">All sources</option>
              {leadSourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Sort By</label>
            <select
              aria-label="Sort vehicles"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="mt-1 block h-8 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {sortOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 3 — Date Added / Total Parts Value */}
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div>
            <label>Date Added</label>
            <div
              className={classNames(
                "mt-1",
                // Tighten the datepicker's input chrome so the calendar toggle
                // sits flush next to the date text instead of floating at the
                // far edge of a wide w-64 field.
                "[&_input]:h-8 [&_input]:w-48 [&_input]:py-1 [&_input]:pl-2 [&_input]:pr-8 [&_input]:text-xs",
                "[&_button]:!px-2"
              )}
            >
              <CustomDatePicker
                dates={dates}
                handleDatesChange={handleDatesChange}
                inputClassName="h-8 w-48 rounded-md border border-slate-300 bg-white py-1 pl-2 pr-8 text-xs text-slate-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label>Total Parts Value (£)</label>
            <div className="mt-1 flex items-center gap-1.5">
              <div className="relative w-24">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-1.5 text-slate-400">
                  <CurrencyPoundIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={minValue}
                  onChange={(e) => setMinValue(e.target.value)}
                  placeholder="Min"
                  aria-label="Minimum total parts value"
                  className="block h-8 w-full rounded-md border border-slate-300 bg-white py-1 pl-6 pr-1.5 text-xs text-slate-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <span className="text-[11px] font-medium text-slate-400">to</span>
              <div className="relative w-24">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-1.5 text-slate-400">
                  <CurrencyPoundIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={maxValue}
                  onChange={(e) => setMaxValue(e.target.value)}
                  placeholder="Max"
                  aria-label="Maximum total parts value"
                  className="block h-8 w-full rounded-md border border-slate-300 bg-white py-1 pl-6 pr-1.5 text-xs text-slate-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {filterBanner}

      <div
        className="mt-4 flex flex-wrap gap-1 border-b border-slate-200"
        role="tablist"
        aria-label="Vehicle filter tabs"
      >
        {tabs.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveTab(tab.key)}
              className={classNames(
                "relative -mb-px rounded-t-md border border-b-0 px-4 py-2 text-sm font-medium transition-colors",
                active
                  ? "border-slate-200 bg-white text-primary-700 shadow-sm"
                  : "border-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              )}
            >
              {tab.label}
              {active && (
                <span
                  className="absolute inset-x-0 -bottom-px h-0.5 rounded-t bg-primary-500"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>

      <PagedTable
        // Bumping the key on sort change re-mounts PagedTable so the new
        // `initialSort` is picked up — PagedTable keeps `sorting` in internal
        // state so there's no controlled way to update it otherwise.
        key={`awaiting-inventory-${sortBy}`}
        url="/vehicle/partial"
        columns={cols}
        pageMode={PageMode.Stock}
        queryLabel="Search any vehicle details"
        onRowClick={(row: IVehicle) => {
          // Awaiting-inventory is a vehicle-centric workflow — operators
          // expect to land on the vehicle page (where the check-in, parts
          // and intelligence live) rather than the originating lead record.
          openInNewTab(`/vehicles/${row.id}`);
        }}
        showTotal={true}
        paginationAtBottom={true}
        additionalBody={additionalBody}
        initialSort={[{ id: activeSort.id, desc: activeSort.desc }]}
        expandedRow={(vehicle) => (
          <div className="flex flex-wrap gap-x-10 gap-y-3 bg-slate-50 p-4 text-sm">
            <div className="flex flex-col">
              <span className="font-semibold text-slate-600">Vehicle Notes</span>
              <span className="text-slate-700">{vehicle.notes || "—"}</span>
            </div>

            <div className="flex flex-col">
              <span className="font-semibold text-slate-600">Office Notes</span>
              <span className="text-slate-700">
                {vehicle.leads[0]?.officeNotes || "—"}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="font-semibold text-slate-600">Driver Notes</span>
              <span className="text-slate-700">
                {vehicle.leads[0]?.driverNotes || "—"}
              </span>
            </div>
          </div>
        )}
        rowColor={(row) =>
          leadManagementEnabled && row.leads.length == 0
            ? "bg-primary-50 hover:bg-primary-100"
            : ""
        }
      />
    </>
  );
}
